// Browse-surface ownership helpers (Inbox vs Ahead).
//
// Information architecture: the Today headline owns current state, Ahead owns everything that is
// "coming up" (time-anchored / future-dated), and Inbox owns things to check, receive, or hold.
// To avoid duplicate ownership of "what's coming up", upcoming ("week") items are intentionally
// NOT surfaced in Inbox — they live in Ahead only.
import { isOverdue, isDueToday, startOfToday } from "./resurface";

export function bucketOf(e) {
  if (e.needs_review) return "review";
  if (e.type === "NOTE" || e.type === "JOURNAL") return "notes";
  if (e.urgency === "NOW" || e.urgency === "TODAY") return "today";
  if (e.urgency === "THIS_WEEK") return "week";
  return "later";
}

// My-Inbox sections, mutually exclusive by priority: today > handed (unseen) > held-for-later.
// "week" (upcoming) items are claimed out so they never render in Inbox and never leak into
// "later" — Ahead is their single browse surface.
export function inboxMineSections(mineActive) {
  const overdue = mineActive.filter(isOverdue);
  const dueToday = mineActive.filter((e) => !isOverdue(e) && isDueToday(e));
  const resurfaced = new Set([...overdue, ...dueToday].map((e) => e.id));
  const buckets = { review: [], today: [], week: [], later: [], notes: [] };
  mineActive.forEach((e) => {
    if (!resurfaced.has(e.id)) buckets[bucketOf(e)].push(e);
  });

  const claimed = new Set();
  const claim = (arr) => arr.filter((e) => (claimed.has(e.id) ? false : (claimed.add(e.id), true)));
  const today = claim([...overdue, ...dueToday, ...buckets.today]);
  const handed = claim(mineActive.filter((e) => e.unseen));
  claim(buckets.week); // upcoming -> owned by Ahead
  const later = claim([...buckets.review, ...buckets.later, ...buckets.notes]);
  return { today, handed, later };
}

export const isShared = (e) =>
  e.visibility === "shared" || e.owner === "partner" || e.owner === "shared";

export const SCHEDULE_TYPES = new Set(["TASK", "REMINDER", "CALENDAR_SUGGESTION", "LATER"]);

// Forward-looking, schedule-worthy split for Ahead: future-dated (sorted) + undated.
// `scopedActive` is the already scope-filtered set of active, schedule-type items.
export function aheadForward(scopedActive) {
  const t0 = startOfToday();
  const dated = scopedActive
    .filter((e) => e.suggested_datetime && new Date(e.suggested_datetime) >= t0)
    .sort((a, b) => new Date(a.suggested_datetime) - new Date(b.suggested_datetime));
  const undated = scopedActive.filter((e) => !e.suggested_datetime);
  return { dated, undated };
}
